import { SignatureModel } from '../../../../../types';
import SharedReviewLayout, {
  ReviewSection,
} from '../../../../shared/shared-review-layout';
import { useStore } from '../../../state/zustand-store';
import HtmlPreview from '../../../../shared/html-preview';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopy from '@mui/icons-material/ContentCopy';

interface StepSignatureTemplateReviewProps {
  selectedTemplate: Partial<SignatureModel>;
  currentAction: string;
}

export default function StepSignatureTemplateReview({
  selectedTemplate,
  currentAction,
}: StepSignatureTemplateReviewProps) {
  const { setSnackbar } = useStore();
  const times = selectedTemplate.times || [];
  const dates = selectedTemplate.dates || [];
  const sections: ReviewSection[] = [
    {
      leftWidth: {
        columnWidth: 5.2,
        labelWidth: 7.5,
        valueWidth: 4.5,
      },
      rightWidth: {
        columnWidth: 6.25,
        labelWidth: 7.5,
        valueWidth: 4.5,
      },
      title: 'Basic Information',
      leftItems: [
        { label: 'Template Name', value: selectedTemplate.templateName },
        { label: 'Description', value: selectedTemplate.description },
        {
          label: 'Manual Run',
          value: selectedTemplate.manualRun,
        },
        {
          label: 'Scheduled Run',
          value: selectedTemplate.scheduled,
        },
      ],
      rightItems: [
        {
          label: 'Created Date',
          value: selectedTemplate.createdDate
            ? selectedTemplate.createdDate
            : '',
          variant: 'date',
          isVisible: Boolean(
            selectedTemplate.createdDate && currentAction === 'details'
          ),
        },
        {
          label: 'Last Ran On',
          value: selectedTemplate.lastRunDate
            ? selectedTemplate.lastRunDate
            : 'Never',
          variant: 'date',
          isVisible: currentAction === 'details',
        },
        {
          label: 'Apply to All Send As Addresses',
          value: selectedTemplate.runOnAliases,
          isVisible: Boolean(selectedTemplate.runOnAliases),
        },
        {
          label: 'Apply to Both Primary and Default Send As Addresses',
          value: selectedTemplate.runOnDefaultAlias,
          isVisible: Boolean(selectedTemplate.runOnDefaultAlias),
        },
        {
          label: 'Apply to Only Primary Send As Address',
          value: selectedTemplate.runOnPrimaryAlias,
          isVisible: Boolean(selectedTemplate.runOnPrimaryAlias),
        },
        {
          label: 'Use send as account for signature template',
          value: selectedTemplate.useSendAsAccountForTemplate,
        },
      ],
    },
    {
      title: '',
      leftItems: [
        {
          label: 'Emails to Notify',
          value: selectedTemplate.emailsToNotify?.map((user) => ({
            email: user,
            type: 'Unknown' as const,
          })),
          variant: 'chip',
          flex: true,
          isVisible: Boolean(
            selectedTemplate.notifyViaEmail &&
              selectedTemplate.emailsToNotify &&
              selectedTemplate.emailsToNotify.length > 0
          ),
        },
      ],
    },
    {
      title: 'Templates Preview',
      headerButton: currentAction === 'details' && (
        <Tooltip arrow title="Copy">
          <span>
            <IconButton
              size="small"
              onClick={async (e) => {
                e.stopPropagation();

                await navigator.clipboard.writeText(
                  selectedTemplate.templateHtml || ''
                );
                setSnackbar({
                  open: true,
                  severity: 'info',
                  message: 'Template Html has been copied to clipboard.',
                });
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      ),
      leftItems: [
        {
          value: (
            <HtmlPreview
              html={selectedTemplate.templateHtml || ''}
              width={550}
              minHeight={1}
              showTitle={false}
            />
          ),
        },
      ],
    },
    {
      title: 'Targets',
      leftItems: [
        { label: 'Target Type', value: selectedTemplate.sourceType },
        {
          label: 'Targets',
          value: selectedTemplate.allSource
            ? 'All'
            : selectedTemplate.sources && selectedTemplate.sources.length > 0
            ? selectedTemplate.sources.map((target) => ({
                email: target,
                type: selectedTemplate.sourceType as
                  | 'User'
                  | 'Group'
                  | 'OrgUnit'
                  | 'Domain',
              }))
            : '',
          variant: selectedTemplate.allSource ? 'default' : 'chip',
          flex: selectedTemplate.allSource ? false : true,
        },
      ],
    },

    ...(selectedTemplate.scheduled
      ? [
          {
            title: 'Schedule',
            leftItems: [
              {
                label: 'Times',
                value: times.map((time) =>
                  parseInt(time) === 0
                    ? '12 AM'
                    : parseInt(time) < 12
                    ? `${time} AM`
                    : parseInt(time) === 12
                    ? '12 PM'
                    : `${parseInt(time) - 12} PM`
                ),
              },
              {
                label: 'Dates',
                value: dates.map((d) => d),
              },
              {
                label: 'Expiration Date',
                value: selectedTemplate.scheduleEndDate
                  ? new Date(
                      selectedTemplate.scheduleEndDate
                    ).toLocaleDateString()
                  : '',
                isVisible: !!(
                  selectedTemplate.scheduleEndDate &&
                  selectedTemplate.scheduleEndDate !== 0
                ),
              },
            ],
          },
        ]
      : []),
  ];

  return <SharedReviewLayout sections={sections} />;
}
